import { useState } from "react";
import { Calendar } from "lucide-react";
import { cn } from "../../lib/cn";
import { DateCalendar, formatDateDisplay } from "./DateCalendar";
import { inputStyles } from "./Input";
import { usePopoverId } from "./Popover";
import { DropdownPortal, useDropdownPanel } from "./useDropdownPanel";

export function DatePicker({
  value = "",
  onChange,
  disabled = false,
  minDate,
  className,
}) {
  const [open, setOpen] = useState(false);
  const panelId = usePopoverId();
  const { triggerRef, panelRef, position } = useDropdownPanel(open, setOpen);

  const openPanel = () => {
    if (disabled) return;
    setOpen(true);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={panelId}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
        className={cn(
          inputStyles,
          "flex w-full items-center justify-between gap-2 text-left cursor-pointer",
          open && "ring-2 ring-ring border-transparent",
          disabled && "cursor-not-allowed",
          className,
        )}
      >
        <span className={cn(!value && "text-muted-foreground")}>
          {formatDateDisplay(value)}
        </span>
        <Calendar size={16} className="shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <DropdownPortal>
          <div
            ref={panelRef}
            data-picker-dropdown
            role="dialog"
            aria-labelledby={panelId}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: Math.max(position.width, 288),
              zIndex: 60,
            }}
            className="rounded-xl border border-border bg-card p-3 shadow-lg"
          >
            <DateCalendar
              value={value}
              disabled={disabled}
              minDate={minDate}
              onChange={(nextValue) => {
                onChange?.(nextValue);
                setOpen(false);
              }}
            />
          </div>
        </DropdownPortal>
      )}
    </>
  );
}

export {
  DateCalendar,
  formatDateDisplay,
  parseDateValue,
  toDateValue,
} from "./DateCalendar";
